import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../api/admin';
import { Mail, Search, Send, X, AlertTriangle, CheckCircle, Loader } from 'lucide-react';

const SEARCH_DEBOUNCE_MS = 350;

const AdminPlatformEmail = () => {
  const [mailStatus, setMailStatus] = useState({
    configured: false,
    fromAddress: '',
    usersWithEmailCount: 0,
    maxSendToAll: 10000,
  });
  const [sendToAll, setSendToAll] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatusLoading(true);
      try {
        const res = await adminApi.getPlatformEmailStatus();
        if (!cancelled && res?.success) {
          setMailStatus({
            configured: !!res.configured,
            fromAddress: res.fromAddress || '',
            usersWithEmailCount: Number(res.usersWithEmailCount) || 0,
            maxSendToAll: Number(res.maxSendToAll) || 10000,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setMessage({ type: 'error', text: 'Impossibile leggere lo stato SMTP.' });
        }
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const runSearch = useCallback(async (q) => {
    if (!q) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await adminApi.getUsers({ search: q }, 30, 0);
      if (res?.success) {
        setSearchResults(res.users || []);
      } else {
        setSearchResults([]);
      }
    } catch (e) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!debouncedSearch) {
      setSearchResults([]);
      return;
    }
    runSearch(debouncedSearch);
  }, [debouncedSearch, runSearch]);

  const addUser = (u) => {
    if (selected.some((s) => s.id === u.id)) return;
    setSelected((prev) => [...prev, { id: u.id, username: u.username, email: u.email || '' }]);
    setMessage({ type: '', text: '' });
  };

  const removeUser = (id) => {
    setSelected((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    if (!mailStatus.configured) {
      setMessage({
        type: 'error',
        text: 'SMTP non configurato sul server. Controlla SMTP_HOST, SMTP_USER, SMTP_PASS nel backend.',
      });
      return;
    }
    if (!sendToAll && selected.length === 0) {
      setMessage({ type: 'error', text: 'Seleziona almeno un destinatario oppure attiva «Tutti».' });
      return;
    }
    if (sendToAll && mailStatus.usersWithEmailCount > mailStatus.maxSendToAll) {
      setMessage({
        type: 'error',
        text: `Troppi utenti con email (${mailStatus.usersWithEmailCount}). Limite «tutti»: ${mailStatus.maxSendToAll}.`,
      });
      return;
    }
    if (!subject.trim() || !body.trim()) {
      setMessage({ type: 'error', text: 'Oggetto e messaggio sono obbligatori.' });
      return;
    }
    const confirmText = sendToAll
      ? `Inviare l'email a TUTTI gli utenti con email (${mailStatus.usersWithEmailCount} indicativi)?`
      : `Inviare l'email a ${selected.length} utente/i dall'indirizzo ufficiale?`;
    if (!window.confirm(confirmText)) return;

    setSending(true);
    try {
      const res = await adminApi.sendPlatformEmail({
        ...(sendToAll ? { sendToAll: true } : { userIds: selected.map((s) => s.id) }),
        subject: subject.trim(),
        body: body.trim(),
      });
      if (res?.success) {
        const parts = [
          res.sendToAll ? 'Modalità: tutti gli utenti con email' : null,
          `Invii riusciti: ${res.sent}`,
          res.failed ? `Falliti: ${res.failed}` : null,
          res.skippedNoEmail ? `Senza email: ${res.skippedNoEmail}` : null,
          !res.sendToAll && res.notFoundUserIds?.length ? `ID non trovati: ${res.notFoundUserIds.join(', ')}` : null,
        ].filter(Boolean);
        setMessage({ type: 'success', text: parts.join(' · ') });
      }
    } catch (err) {
      const txt = err?.message || 'Invio fallito';
      setMessage({ type: 'error', text: txt });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Mail className="w-9 h-9 text-yellow-500" />
        <div>
          <h1 className="text-3xl font-bold text-white">Email da piattaforma</h1>
          <p className="text-gray-400 text-sm mt-1">
            Invio tramite SMTP ufficiale (stesso canale delle altre email di sistema).
          </p>
        </div>
      </div>

      {statusLoading ? (
        <div className="flex items-center gap-2 text-gray-400">
          <Loader className="w-5 h-5 animate-spin" />
          Verifica configurazione…
        </div>
      ) : (
        <div
          className={`rounded-lg border p-4 flex items-start gap-3 ${
            mailStatus.configured
              ? 'bg-green-900/20 border-green-700/50'
              : 'bg-red-900/20 border-red-700/50'
          }`}
        >
          {mailStatus.configured ? (
            <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          )}
          <div className="text-sm">
            <p className="text-white font-medium">
              {mailStatus.configured ? 'SMTP configurato' : 'SMTP non configurato'}
            </p>
            <p className="text-gray-300 mt-1">
              Mittente: <span className="text-yellow-400 font-mono">{mailStatus.fromAddress || '—'}</span>
              {' '}(variabile <code className="text-gray-400">SMTP_FROM</code> o account SMTP)
            </p>
          </div>
        </div>
      )}

      {message.text && (
        <div
          className={`rounded-lg p-3 text-sm ${
            message.type === 'success'
              ? 'bg-green-900/30 text-green-200 border border-green-700/40'
              : 'bg-red-900/30 text-red-200 border border-red-700/40'
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSend} className="space-y-6">
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 space-y-3">
          <label className="block text-sm font-semibold text-gray-300">Destinatari</label>

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={sendToAll}
              disabled={!statusLoading && mailStatus.usersWithEmailCount > mailStatus.maxSendToAll}
              onChange={(e) => {
                const on = e.target.checked;
                setSendToAll(on);
                if (on) {
                  setSelected([]);
                  setSearch('');
                  setDebouncedSearch('');
                  setSearchResults([]);
                }
                setMessage({ type: '', text: '' });
              }}
              className="mt-1 rounded border-gray-600 text-yellow-500 focus:ring-yellow-500"
            />
            <span className="text-sm text-gray-300">
              <span className="font-medium text-white">Tutti</span>
              {' — '}
              invia a tutti gli utenti che hanno un&apos;email nel profilo (
              <span className="text-yellow-400">{mailStatus.usersWithEmailCount}</span>
              {statusLoading ? '' : ` / max ${mailStatus.maxSendToAll}`}
              ).
            </span>
          </label>
          {mailStatus.usersWithEmailCount > mailStatus.maxSendToAll && !statusLoading && (
            <p className="text-xs text-amber-400">
              Il flag «Tutti» è disattivato: superi il limite massimo consentito per un singolo invio.
            </p>
          )}

          {!sendToAll && (
            <>
              <p className="text-xs text-gray-500">
                Cerca per username o email, poi clicca per aggiungere. Puoi selezionare più utenti.
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca utente (username o email)…"
                  className="w-full pl-10 pr-3 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none"
                />
              </div>
              {searchLoading && (
                <p className="text-xs text-gray-500 flex items-center gap-2">
                  <Loader className="w-3 h-3 animate-spin" /> Ricerca…
                </p>
              )}
              {debouncedSearch && !searchLoading && searchResults.length > 0 && (
                <ul className="max-h-48 overflow-y-auto rounded-lg border border-gray-600 divide-y divide-gray-700 bg-gray-900">
                  {searchResults.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => addUser(u)}
                        disabled={selected.some((s) => s.id === u.id)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-800 disabled:opacity-40 text-sm"
                      >
                        <span className="text-white font-medium">{u.username}</span>
                        <span className="text-gray-400 ml-2">{u.email || '(nessuna email)'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {debouncedSearch && !searchLoading && searchResults.length === 0 && (
                <p className="text-sm text-gray-500">Nessun risultato.</p>
              )}

              {selected.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {selected.map((s) => (
                    <span
                      key={s.id}
                      className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-gray-700 text-xs text-white"
                    >
                      {s.username}
                      <button
                        type="button"
                        onClick={() => removeUser(s.id)}
                        className="p-0.5 rounded-full hover:bg-gray-600"
                        aria-label={`Rimuovi ${s.username}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 space-y-3">
          <label className="block text-sm font-semibold text-gray-300">Oggetto</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white focus:border-yellow-500 focus:outline-none"
            placeholder="Oggetto dell'email"
          />
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 space-y-3">
          <label className="block text-sm font-semibold text-gray-300">Messaggio</label>
          <p className="text-xs text-gray-500">
            Il testo viene inviato come email di testo; a ogni destinatario viene anteposto &quot;Ciao [username],&quot;.
          </p>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white focus:border-yellow-500 focus:outline-none font-mono text-sm"
            placeholder="Scrivi il messaggio…"
          />
        </div>

        <button
          type="submit"
          disabled={sending || !mailStatus.configured}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-yellow-500 text-gray-900 font-semibold hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          Invia email
        </button>
      </form>
    </div>
  );
};

export default AdminPlatformEmail;
